import { useEffect, useRef, useState } from 'react';

/**
 * A [lng, lat] to bias address autocomplete toward, or null if we have none.
 *
 * Deliberately silent: it reads the rider's position **only when geolocation
 * permission has already been granted**, checked through the Permissions API
 * first. Asking for location the moment someone lands on the booking page — to
 * improve a dropdown they have not opened yet — trades a permission prompt the
 * user did not ask for against a benefit they cannot yet see, and a denial is
 * sticky. The explicit "use current location" button is where a prompt belongs.
 *
 * Browsers without `navigator.permissions` (older Safari) simply get no bias
 * rather than a prompt; the caller falls back to the pickup's coordinates.
 */
export const useLocationBias = (): [number, number] | null => {
  const [bias, setBias] = useState<[number, number] | null>(null);
  // StrictMode double-invokes effects; one position read is enough.
  const requested = useRef(false);

  useEffect(() => {
    if (requested.current) return;
    requested.current = true;

    let cancelled = false;

    const read = () => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (cancelled) return;
          setBias([position.coords.longitude, position.coords.latitude]);
        },
        () => {
          // Nothing to do — no bias is a supported state, not an error worth
          // surfacing for an autocomplete nicety.
        },
        // Coarse and cheap: this only has to name a city, and a cached fix
        // from the last five minutes is more than good enough for ranking.
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 5 * 60 * 1000 },
      );
    };

    if (!navigator.geolocation) return;

    if (!navigator.permissions?.query) {
      // Cannot tell whether asking would prompt, so don't ask.
      return;
    }

    navigator.permissions
      .query({ name: 'geolocation' as PermissionName })
      .then((status) => {
        if (cancelled) return;
        if (status.state === 'granted') read();
        // If the user grants it later (via the current-location button), pick
        // the bias up then rather than making them reload.
        status.onchange = () => {
          if (!cancelled && status.state === 'granted') read();
        };
      })
      .catch(() => {
        /* Permissions API present but unhappy — stay silent. */
      });

    return () => {
      cancelled = true;
    };
  }, []);

  return bias;
};
